import { faQuoteRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Image from '@tiptap/extension-image';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useState } from 'react';
import styled from 'styled-components';
import { ImageInsertDialog } from './ImageInsertDialog';
import { richContentCss } from './RichContent';

// Seeded once from `value`; remount (via `key`) to edit a different record.
export function RichTextEditor({
  value,
  onChange,
  ariaLabel = 'Content',
  compact = false,
}: {
  value: string;
  onChange: (html: string) => void;
  ariaLabel?: string;
  // A short box for secondary fields such as hints and feedback messages.
  compact?: boolean;
}) {
  const [pickingImage, setPickingImage] = useState(false);
  const editor = useEditor({
    // Avoid render-time flush warnings in React 19 StrictMode.
    immediatelyRender: false,
    // StarterKit v3 bundles Link; Image isn't bundled, so add it.
    extensions: [StarterKit.configure({ link: { openOnClick: false } }), Image],
    content: value,
    editorProps: {
      attributes: { 'aria-label': ariaLabel, class: 'rich-text-content' },
    },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  if (!editor) return null;

  const promptLink = () => {
    const prev = editor.getAttributes('link').href ?? '';
    const url = window.prompt('Link URL', prev);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const insertImage = (src: string) => {
    setPickingImage(false);
    editor.chain().focus().setImage({ src }).run();
  };

  return (
    <Wrapper $compact={compact}>
      <Toolbar>
        <ToolButton
          type="button"
          aria-label="Bold"
          $active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          B
        </ToolButton>
        <ToolButton
          type="button"
          aria-label="Italic"
          $active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <em>I</em>
        </ToolButton>
        <ToolButton
          type="button"
          aria-label="Heading 2"
          $active={editor.isActive('heading', { level: 2 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
        >
          H2
        </ToolButton>
        <ToolButton
          type="button"
          aria-label="Heading 3"
          $active={editor.isActive('heading', { level: 3 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
        >
          H3
        </ToolButton>
        <ToolButton
          type="button"
          aria-label="Bullet list"
          $active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          • List
        </ToolButton>
        <ToolButton
          type="button"
          aria-label="Numbered list"
          $active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          1. List
        </ToolButton>
        <ToolButton
          type="button"
          aria-label="Quote"
          $active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <FontAwesomeIcon icon={faQuoteRight} />
        </ToolButton>
        <ToolButton
          type="button"
          aria-label="Link"
          $active={editor.isActive('link')}
          onClick={promptLink}
        >
          Link
        </ToolButton>
        <ToolButton
          type="button"
          aria-label="Image"
          onClick={() => setPickingImage(true)}
        >
          Image
        </ToolButton>
      </Toolbar>
      <EditorContent editor={editor} />
      <ImageInsertDialog
        open={pickingImage}
        onInsert={insertImage}
        onClose={() => setPickingImage(false)}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div<{ $compact: boolean }>`
  border: 1px solid var(--grey-300);
  border-radius: 0.375rem;
  background-color: var(--grey-50);
  overflow: hidden;

  .rich-text-content {
    ${richContentCss}
    min-height: ${(p) => (p.$compact ? '4.5rem' : '16rem')};
    outline: none;
    padding: 0.75rem;
  }

  .rich-text-content:focus {
    outline: 2px solid var(--blue);
    outline-offset: -1px;
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  padding: 0.5rem;
  border-bottom: 1px solid var(--grey-300);
  background-color: var(--grey-75);
`;

const ToolButton = styled.button<{ $active?: boolean }>`
  min-width: 2rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid ${(p) => (p.$active ? 'var(--blue)' : 'var(--grey-300)')};
  border-radius: 0.25rem;
  background-color: ${(p) => (p.$active ? 'var(--blue)' : 'var(--grey-50)')};
  color: ${(p) => (p.$active ? 'white' : 'var(--grey-800)')};
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: var(--blue);
  }
`;
